import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Calendar, MapPin, Clock, Users, Euro, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../App';

const EventsPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });

  const fetchEvents = useCallback(async () => {
    try {
      setError(null);
      const response = await axios.get(`${API}/events`);
      setEvents(response.data || []);
    } catch (err) {
      setError('Impossible de charger les événements');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleRegister = (event) => {
    setSelectedEvent(event);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/events/${selectedEvent.id}/register`, {
        ...formData,
        event_id: selectedEvent.id,
      });
      toast.success('Inscription enregistrée ! Vous recevrez une confirmation.');
      setDialogOpen(false);
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      if (error.response?.data?.detail === 'Événement complet') {
        toast.error('Désolé, cet événement est complet.');
      } else {
        toast.error('Erreur lors de l\'inscription');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  const isPastEvent = (dateStr) => {
    return new Date(dateStr) < new Date();
  };

  return (
    <div className="min-h-screen bg-black pt-24">
      {/* Hero Section */}
      <section className="py-16 border-b border-white/10">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <span className="font-['Barlow'] text-[#FACC15] text-sm uppercase tracking-[0.3em] mb-4 block">
              Rejoignez-nous
            </span>
            <h1 className="font-['Teko'] text-5xl md:text-7xl text-white uppercase leading-none mb-6">
              NOS ÉVÉNEMENTS
            </h1>
            <p className="font-['Barlow'] text-gray-400 max-w-2xl mx-auto text-lg">
              Participez à nos événements, stages et rencontres autour du monde de la cascade.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Events List */}
      <section className="py-24">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="spinner" />
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-20">
              <Calendar size={64} className="mx-auto text-gray-600 mb-6" />
              <p className="font-['Barlow'] text-gray-500 text-xl">
                Aucun événement prévu pour le moment.
              </p>
              <p className="font-['Barlow'] text-gray-600 mt-2">
                Revenez bientôt pour découvrir nos prochains événements !
              </p>
            </div>
          ) : (
            <div className="space-y-8">
              {events.map((event, index) => (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className={`bg-[#09090b] border border-white/10 overflow-hidden ${isPastEvent(event.date) ? 'opacity-60' : 'hover:border-[#FACC15]/30'} transition-all duration-300`}
                >
                  <div className="md:flex">
                    {/* Image */}
                    <div className="md:w-1/3 aspect-video md:aspect-auto">
                      {event.image_url ? (
                        <img
                          src={`${process.env.REACT_APP_BACKEND_URL}${event.image_url}`}
                          alt={event.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full min-h-[200px] flex items-center justify-center bg-gradient-to-br from-[#FACC15]/20 to-[#FACC15]/5">
                          <Calendar size={64} className="text-[#FACC15]" />
                        </div>
                      )}
                    </div>
                    
                    {/* Content */}
                    <div className="md:w-2/3 p-8">
                      <div className="flex flex-wrap gap-4 mb-4">
                        <span className="flex items-center gap-2 text-[#FACC15] font-['Barlow'] text-sm">
                          <Calendar size={16} />
                          {formatDate(event.date)}
                        </span>
                        {event.time && (
                          <span className="flex items-center gap-2 text-gray-400 font-['Barlow'] text-sm">
                            <Clock size={16} />
                            {event.time}
                          </span>
                        )}
                        <span className="flex items-center gap-2 text-gray-400 font-['Barlow'] text-sm">
                          <MapPin size={16} />
                          {event.location}
                        </span>
                      </div>
                      
                      <h2 className="font-['Teko'] text-3xl text-white uppercase mb-4">
                        {event.title}
                      </h2>
                      
                      <p className="font-['Barlow'] text-gray-300 mb-6 leading-relaxed">
                        {event.description}
                      </p>
                      
                      <div className="flex flex-wrap items-center gap-6">
                        {event.price > 0 && (
                          <span className="flex items-center gap-2 text-[#FACC15] font-['Teko'] text-2xl">
                            <Euro size={20} />
                            {event.price}
                          </span>
                        )}
                        {event.max_participants && (
                          <span className="flex items-center gap-2 text-gray-400 font-['Barlow'] text-sm">
                            <Users size={16} />
                            {event.max_participants} places max
                          </span>
                        )}
                        
                        {!isPastEvent(event.date) ? (
                          <Button
                            onClick={() => handleRegister(event)}
                            className="bg-[#FACC15] text-black font-bold uppercase tracking-wider hover:bg-[#EAB308] ml-auto"
                          >
                            S'inscrire
                          </Button>
                        ) : (
                          <span className="font-['Barlow'] text-gray-500 ml-auto">
                            Événement passé
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Registration Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#09090b] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Teko'] text-2xl text-white">
              Inscription à l'événement
            </DialogTitle>
          </DialogHeader>
          {selectedEvent && (
            <div className="mb-4 p-4 bg-black/50 border border-white/10">
              <h3 className="font-['Teko'] text-xl text-[#FACC15]">{selectedEvent.title}</h3>
              <p className="font-['Barlow'] text-gray-400 text-sm">
                {formatDate(selectedEvent.date)} {selectedEvent.time && `à ${selectedEvent.time}`}
              </p>
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">Nom complet *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="bg-black border-white/20 text-white"
                required
              />
            </div>
            <div>
              <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">Email *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="bg-black border-white/20 text-white"
                required
              />
            </div>
            <div>
              <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">Téléphone *</Label>
              <Input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                className="bg-black border-white/20 text-white"
                required
              />
            </div>
            <div>
              <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">Message (optionnel)</Label>
              <Textarea
                value={formData.message}
                onChange={(e) => setFormData({...formData, message: e.target.value})}
                className="bg-black border-white/20 text-white"
                rows={3}
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-[#FACC15] text-black font-bold uppercase tracking-wider hover:bg-[#EAB308] disabled:opacity-50"
            >
              {submitting ? 'Inscription en cours...' : 'Confirmer l\'inscription'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EventsPage;
